import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { WorkflowService } from '../../../../data-access/workflow.service';
import { WorkflowTaskSummary, WorkflowTaskStatus } from '../../../../domain/types/workflow';
import { getStageName, getWorkflowDisplayName, formatYear, getTaskStatusClass, getCaseStatusClass, getDisplayTaskStatus, getDisplayTaskStatusClass, extractDateFromUtcString } from '../workflow-utils';

export type TaskFilter = 'Active' | 'Upcoming' | 'Completed' | 'All';

@Component({
  selector: 'app-my-tasks',
  templateUrl: './my-tasks.component.html',
  styleUrls: ['./my-tasks.component.scss']
})
export class MyTasksComponent implements OnInit {
  allTasks: WorkflowTaskSummary[] = [];
  tasks: WorkflowTaskSummary[] = [];
  loading = true;
  filter: TaskFilter = 'Active';

  getStageName = getStageName;
  getWorkflowDisplayName = getWorkflowDisplayName;
  formatYear = formatYear;
  getStatusClass = getTaskStatusClass;
  getCaseStatusClass = getCaseStatusClass;
  getDisplayTaskStatus = getDisplayTaskStatus;
  getDisplayTaskStatusClass = getDisplayTaskStatusClass;
  extractDateFromUtcString = extractDateFromUtcString;

  constructor(
    private workflowSvc: WorkflowService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  async ngOnInit() {
    const f = this.route.snapshot.queryParamMap.get('filter');
    if (f === 'Active' || f === 'Upcoming' || f === 'Completed' || f === 'All') {
      this.filter = f;
    }
    await this.loadTasks();
  }

  async loadTasks() {
    this.loading = true;
    try {
      // Fetch the full set once; the Active/Upcoming/Completed/All filter is
      // applied client-side so switching tabs doesn't round-trip the server.
      this.allTasks = await this.workflowSvc.getMyTasks(true);
      this.applyFilter();
    } catch (err) {
      console.error('Failed to load tasks', err);
    } finally {
      this.loading = false;
    }
  }

  private applyFilter() {
    this.tasks = this.allTasks.filter(t => {
      switch (this.filter) {
        case 'All': return true;
        case 'Completed': return t.status === WorkflowTaskStatus.Completed;
        case 'Upcoming': return t.status === WorkflowTaskStatus.Active && t.gateStatus === 'Upcoming';
        // Active = open work, including past due (opened but not yet completed).
        case 'Active': return t.status === WorkflowTaskStatus.Active && t.gateStatus !== 'Upcoming';
      }
    });
  }

  // Only surface the Opens On column when at least one task in view actually has
  // an opens-on date to show (an upcoming, date-configured task) — same pattern
  // as the Completed On / Case Status columns.
  get hasOpensOn(): boolean {
    return this.tasks.some(t => t.gateStatus === 'Upcoming' && !!t.gateOpenDate);
  }

  // Show the Case Status / Completed On columns once completed tasks are in view.
  get showCompleted(): boolean {
    return this.filter === 'Completed' || this.filter === 'All';
  }

  setFilter(filter: TaskFilter) {
    if (filter === this.filter) return;
    this.filter = filter;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { filter, completed: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    this.applyFilter();
  }

  openTask(task: WorkflowTaskSummary) {
    this.router.navigate(['/review/task', task.id]);
  }

  viewPacket(task: WorkflowTaskSummary) {
    this.router.navigate(['/review/packet', task.caseId]);
  }

  isActionable(task: WorkflowTaskSummary): boolean {
    // A task is actionable only while its gate is open. Upcoming/Past Due tasks
    // are shown but open read-only — the assignee can't act until
    // the window opens or an extension reopens it.
    return task.status === WorkflowTaskStatus.Active
      && task.gateStatus !== 'Upcoming'
      && task.gateStatus !== 'PastDue';
  }
}
